import { ClipboardList, Search, BadgeCheck, FileCheck2, Home as HomeIcon, Wind, Layers } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import FAQ from "@/components/FAQ";
import VEUForm from "@/components/forms/VEUForm";
import { Eyebrow, GradientText } from "@/components/Brand";
import { Reveal } from "@/lib/motion";

const STEPS = [
  { icon: ClipboardList, title: "Tell Us What You Currently Have", text: "Provide details of the heating or cooling equipment currently installed at the property." },
  { icon: Search, title: "Select an Eligible Upgrade", text: "We discuss suitable air conditioning options and whether the proposed equipment may meet current VEU requirements." },
  { icon: BadgeCheck, title: "Eligibility Is Confirmed", text: "Where a VEU incentive is proposed, eligibility is checked with the participating Accredited Provider before installation." },
  { icon: FileCheck2, title: "Installation & Documentation", text: "The eligible system is installed and required documentation is supplied for processing through the participating Accredited Provider." },
];

const SYSTEMS = [
  { icon: HomeIcon, title: "Ducted Reverse Cycle" },
  { icon: Wind, title: "Split Systems" },
  { icon: Layers, title: "Multi-Head Systems" },
];

const FAQS = [
  { q: "What is the Victorian Energy Upgrades program?", a: "The Victorian Energy Upgrades program is a Victorian Government energy-efficiency program that provides incentives for eligible upgrades." },
  { q: "Is Airconditioning Victoria a VEU Accredited Provider?", a: "No. Airconditioning Victoria Pty Ltd is not currently a VEU Accredited Person. Where applicable, eligible incentive processing is arranged through an independent participating Accredited Provider." },
  { q: "Can Airconditioning Victoria install an eligible system?", a: "Yes. Installation businesses can work with participating Accredited Providers, subject to the applicable VEU requirements and the arrangements applying to the installation." },
  { q: "Is everyone guaranteed a rebate?", a: "No. Eligibility depends on current program rules, existing equipment, new equipment, the property and other applicable requirements." },
  { q: "Should eligibility be checked before installation?", a: "Yes. Customers seeking a VEU incentive should have the proposed eligibility and arrangement confirmed before work begins." },
  { q: "Can rebate values change?", a: "Yes. Incentive values and certificate values may change, so final eligibility and pricing should be confirmed before installation." },
];

export default function VEU() {
  return (
    <>
      <SEO
        title="Victorian Energy Upgrades Air Conditioning Rebates | Airconditioning Victoria"
        description="Learn about potential Victorian Energy Upgrades incentives for eligible reverse-cycle air conditioning installations and replacements."
        path="/veu-rebates"
      />
      <PageHero
        eyebrow="Victorian Energy Upgrades"
        titleLines={["Upgrade your comfort.", <GradientText key="g">Check your VEU eligibility.</GradientText>]}
        intro="The Victorian Energy Upgrades program provides incentives for eligible energy-efficient upgrades, including selected high-efficiency reverse-cycle air conditioning systems."
        ctaText="Check My Eligibility"
        ctaTo="#veu-form"
        secondaryPhone
      />

      {/* DISCLOSURE */}
      <section className="bg-white pb-4 pt-2" data-testid="veu-disclosure-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal>
            <div className="rounded-[18px] border border-cardline border-l-4 border-l-brand-cyan bg-white p-8 shadow-[0_8px_32px_rgba(5,59,117,0.05)]" data-testid="veu-disclosure-card">
              <h2 className="font-display text-xl font-bold text-navy">Important VEU Information</h2>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-mutedblue">
                <p>
                  Airconditioning Victoria Pty Ltd is an air conditioning supplier and installer and
                  is not currently accredited as an Accredited Person under the Victorian Energy
                  Upgrades program.
                </p>
                <p>
                  Where an eligible VEU discount is offered, Airconditioning Victoria works with an
                  independent participating VEU Accredited Provider. The participating Accredited
                  Provider is responsible for applicable certificate creation and VEU program
                  administration.
                </p>
                <p>
                  Eligibility, incentive amounts, approved products, documentation and installation
                  requirements are determined under current VEU program rules and may change.
                  Eligibility must be confirmed before installation.
                </p>
                <p className="font-medium text-ink">
                  Airconditioning Victoria is not affiliated with or endorsed by the Victorian
                  Government.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-white py-24 md:py-32" data-testid="veu-process-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="max-w-2xl">
            <Eyebrow>The Process</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              How the VEU process works.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="group relative h-full rounded-[20px] border border-cardline bg-white p-7 shadow-[0_8px_32px_rgba(5,59,117,0.04)] transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-brand-cyan">
                  <span className="font-display text-5xl font-extrabold text-mist transition-colors duration-300 group-hover:text-ice">
                    0{i + 1}
                  </span>
                  <span className="mt-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-ice">
                    <s.icon className="h-5 w-5 text-brand-blue" />
                  </span>
                  <h3 className="mt-4 font-display text-base font-bold text-navy">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mutedblue">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ELIGIBILITY CHECKLIST */}
      <section className="bg-white pb-24 md:pb-32" data-testid="veu-checklist-section">
        <div className="mx-auto max-w-7xl px-5">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <Reveal>
              <Eyebrow>Before You Commit</Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                VEU eligibility — <GradientText>the quick checklist.</GradientText>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-mutedblue">
                A few important things to understand before proceeding with any VEU-discounted
                installation.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <ul className="space-y-4 rounded-[20px] border border-cardline bg-offwhite p-8">
                {[
                  "The selected system must satisfy current VEU eligibility and product requirements.",
                  "Replacement conditions depend on the equipment currently installed at your property.",
                  "Eligibility must be confirmed before installation begins.",
                  "Where applicable, we work with the participating Accredited Provider before work starts.",
                  "Incentive and certificate values can change over time.",
                ].map((item, i) => (
                  <li key={item} className="flex items-start gap-4" data-testid={`veu-checklist-${i}`}>
                    <span className="font-display text-lg font-extrabold text-gradient-brand">0{i + 1}</span>
                    <p className="text-sm leading-relaxed text-ink">{item}</p>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ELIGIBLE SYSTEMS */}
      <section className="bg-cloud py-24 md:py-32" data-testid="veu-systems-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="max-w-2xl">
            <Eyebrow>Upgrades</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Potentially <GradientText>eligible systems.</GradientText>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {SYSTEMS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="flex h-full items-center gap-4 rounded-[18px] border border-cardline bg-white p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-brand-cyan">
                  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-brand">
                    <s.icon className="h-6 w-6 text-white" />
                  </span>
                  <h3 className="font-display text-sm font-bold uppercase tracking-[0.12em] text-navy">{s.title}</h3>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-sm leading-relaxed text-mutedblue">
              Eligibility depends on the specific product, existing equipment, installation
              configuration, property circumstances and current Victorian Energy Upgrades rules.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FORM */}
      <section id="veu-form" className="bg-offwhite py-24 md:py-32" data-testid="veu-form-section">
        <div className="mx-auto max-w-3xl px-5">
          <Reveal className="text-center">
            <Eyebrow className="justify-center">Eligibility Check</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Check Your Potential Rebate
            </h2>
            <p className="mt-4 text-base leading-relaxed text-mutedblue">
              Complete the form below and our team will review your details. Submitting this form
              does not guarantee eligibility or a specific incentive amount.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <div className="rounded-[20px] border border-cardline bg-white p-7 shadow-[0_16px_50px_rgba(5,59,117,0.07)] md:p-10">
              <VEUForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-24 md:py-32" data-testid="veu-faq-section">
        <div className="mx-auto max-w-4xl px-5">
          <Reveal className="text-center">
            <Eyebrow className="justify-center">Common Questions</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              VEU rebate FAQs.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <FAQ items={FAQS} testidPrefix="veu-faq" />
          </Reveal>
        </div>
      </section>

      <FinalCTA
        heading="Want to know if your upgrade qualifies?"
        text="Talk to our team about your existing system and the air conditioning upgrade you're considering."
      />
    </>
  );
}
